import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { websocketService } from '../services/websocketService';
import { apiService } from '../services/apiService';

const ObservatoryContext = createContext(null);

// Exported so components that want to degrade gracefully when rendered
// outside an ObservatoryProvider (e.g. the focused-observatory card in
// the classic /observatories page) can read the context as `null`
// without crashing.
export { ObservatoryContext };

export const useObservatory = () => {
  const context = useContext(ObservatoryContext);
  if (!context) {
    throw new Error('useObservatory must be used within an ObservatoryProvider');
  }
  return context;
};

export const ObservatoryProvider = ({ observatoryId, children }) => {
  const [observatory, setObservatory] = useState(null);
  const [telemetry, setTelemetry] = useState(null);
  const [observatoryState, setObservatoryState] = useState(null);
  const [currentObservation, setCurrentObservation] = useState(null);
  const [queue, setQueue] = useState([]);
  const [eventLog, setEventLog] = useState([]);
  const [wsStatus, setWsStatus] = useState('disconnected');
  const [guiderHistory, setGuiderHistory] = useState([]);
  const eventLogRef = useRef([]);
  // Set once per observatoryId after we've pulled a fresh snapshot in
  // response to telemetry (see handleTelemetry below). Guards against
  // refetching the observatory document on every 30 s telemetry tick —
  // we only want one refetch when we realise our status cache was stale.
  const refetchedOnLiveTelemetryRef = useRef(false);

  const addLogEntry = useCallback((entry) => {
    const timestamp = entry.timestamp || new Date().toISOString();
    const message = String(entry.message || '');
    const severity = entry.severity || 'info';
    const log = eventLogRef.current;
    const first = log.length > 0 ? log[0] : null;

    // If the newest entry (first) has the same message, just update its timestamp and bump count
    if (first && first.message === message) {
      first.timestamp = timestamp;
      first.repeatCount = (first.repeatCount || 1) + 1;
      eventLogRef.current = log.slice(); // new array ref to trigger re-render
    } else {
      eventLogRef.current = [{
        ...entry,
        message,
        severity,
        timestamp,
        id: Date.now() + Math.random(),
        repeatCount: 1,
      }, ...log.slice(0, 199)];
    }
    setEventLog(eventLogRef.current);
  }, []);

  // Stable fetch callbacks for queue and current observation
  const fetchQueue = useCallback(async () => {
    if (!observatoryId) return;
    try {
      // Scope the query SERVER-side (#1416). This used to fetch a global queue
      // and narrow it here, which cannot work: /v1/queue returned only the first
      // ten documents, so an observatory whose observations sat past that cut
      // showed an empty queue while the Schedule tab -- which does pass
      // observatory_id -- displayed them. A client filter can shrink a list,
      // never recover a row the server already dropped.
      // Include waiting_next so a repetitive observation whose next window is open shows in the queue.
      // A repetitive obs sits in waiting_next between iterations and flips straight to assigned at
      // dispatch (never pending), so asking for pending alone hid it from Operations entirely. The
      // queue endpoint applies the open-window gate to waiting_next, so only ones ready-to-run appear.
      const response = await apiService.get('/v1/queue', {
        params: { observatory_id: observatoryId, status: 'pending,waiting_next', limit: 20 }
      });
      if (response.data?.success) {
        const allQueue = response.data.data?.queue || response.data.data || [];
        setQueue(allQueue);
      }
    } catch (error) {
      console.error('Error fetching queue:', error);
    }
  }, [observatoryId]);

  const fetchCurrentObservation = useCallback(async () => {
    if (!observatoryId) return;
    try {
      const currentObs = await apiService.getObservatoryCurrentObservation(observatoryId);
      setCurrentObservation(currentObs || null);
    } catch (error) {
      console.error('Error fetching current observation:', error);
    }
  }, [observatoryId]);

  // Fetch observatory details. Extracted as a useCallback so we can refetch
  // on demand when the plugin comes online — capabilities, filter names,
  // and equipment_configuration are populated server-side during plugin
  // authentication but aren't broadcast piecemeal, so the initial snapshot
  // captured before the plugin connected would otherwise remain stale.
  const fetchObservatory = useCallback(async () => {
    if (!observatoryId) return;
    try {
      const response = await apiService.get(`/v1/observatories/${observatoryId}`);
      if (response.data?.success) {
        const obs = response.data.data;
        setObservatory(obs);
        if (obs.status === 'offline') {
          setTelemetry(null);
        } else if (obs.equipment_status) {
          setTelemetry({
            equipment_status: obs.equipment_status,
            system_status: obs.system_status,
          });
        }
      }
    } catch (error) {
      console.error('Error fetching observatory:', error);
    }
  }, [observatoryId]);

  useEffect(() => {
    if (!observatoryId) return;

    // Fresh observatory: arm the one-shot telemetry-based refetch again.
    refetchedOnLiveTelemetryRef.current = false;

    const fetchState = async () => {
      try {
        const response = await apiService.get(`/v1/observatory-states/${observatoryId}`);
        if (response.data?.success) {
          setObservatoryState(response.data.data);
        }
      } catch (error) {
        // State endpoint may not exist for all observatories
      }
    };

    fetchObservatory();
    fetchQueue();
    fetchCurrentObservation();
    fetchState();

    // Refresh queue periodically
    const queueInterval = setInterval(fetchQueue, 30000);
    const obsInterval = setInterval(fetchCurrentObservation, 30000);

    return () => {
      clearInterval(queueInterval);
      clearInterval(obsInterval);
    };
  }, [observatoryId, fetchObservatory, fetchQueue, fetchCurrentObservation]);

  // WebSocket subscriptions
  useEffect(() => {
    if (!observatoryId) return;

    const handleStatusChange = (status) => {
      setWsStatus(status);
      // When the socket (re)connects/authenticates after this provider mounted,
      // pull a fresh snapshot. On client-side navigation into the Operations View
      // the singleton socket may already be connected (no fresh burst) or briefly
      // not ready, leaving the queue / current observation empty until a manual
      // browser refresh. Re-syncing on connect closes that gap.
      if (status === 'connected' || status === 'authenticated') {
        fetchObservatory();
        fetchQueue();
        fetchCurrentObservation();
      }
    };

    const handleTelemetry = (data) => {
      if (data.observatory_id === observatoryId) {
        // Merge with existing telemetry to preserve keys not in this update
        setTelemetry(prev => ({
          equipment_status: {
            ...prev?.equipment_status,
            ...data.equipment_status,
          },
          system_status: {
            ...prev?.system_status,
            ...data.system_status,
          },
          timestamp: data.timestamp,
        }));

        // Telemetry arrival is itself proof the plugin is live. The
        // `observatory_status_update` broadcast that normally flips
        // observatory.status to 'online' can be delivered *before* our
        // subscription mounts (common on auto-activation of a just-
        // self-registered observatory: the plugin often finishes
        // authenticating while this component is still running its
        // first render), so relying on it alone leaves the chip
        // showing offline/inactive forever even though real-time data
        // is streaming. Sync status here as a fallback, and pull the
        // fresh observatory document once so capabilities /
        // equipment_configuration / filter names — written during
        // plugin auth but not broadcast piecemeal — fill in too.
        setObservatory(prev => {
          if (!prev) return prev;
          if (prev.status === 'online' && prev.plugin_connected) return prev;
          return { ...prev, status: 'online', plugin_connected: true };
        });
        if (!refetchedOnLiveTelemetryRef.current) {
          refetchedOnLiveTelemetryRef.current = true;
          fetchObservatory();
        }

        // Keep observatory.system_status in sync with telemetry
        if (data.system_status) {
          setObservatory(prev => prev ? {
            ...prev,
            system_status: { ...prev.system_status, ...data.system_status },
          } : prev);
        }

        // Guider state arrives on its own channel — see handleGuiderUpdate below.
        // telemetry_update payloads no longer carry equipment_status.guider.
      }
    };

    // Guider arrives on its own message type (issue #673 follow-up) so it doesn't
    // overload telemetry_update's "full equipment snapshot" contract. Merge the
    // subdoc into telemetry.equipment_status so consumers reading
    // telemetry.equipment_status.guider still work, and accumulate RMS history
    // while actively guiding.
    const handleGuiderUpdate = (data) => {
      if (data.observatory_id !== observatoryId) return;
      const guider = data.guider;
      if (!guider) return;

      setTelemetry(prev => ({
        ...prev,
        equipment_status: {
          ...prev?.equipment_status,
          guider,
        },
        timestamp: data.timestamp,
      }));

      if (guider.connected && guider.guiding && guider.rms_error != null) {
        setGuiderHistory(prev => [
          ...prev.slice(-59),
          {
            time: new Date(data.timestamp || Date.now()).toLocaleTimeString('en-US', { hour12: false }),
            rms: guider.rms_error,
            ra_rms: guider.ra_rms || 0,
            dec_rms: guider.dec_rms || 0,
          },
        ]);
      }
    };

    const handleObservatoryUpdate = (data) => {
      if (data.observatory_id === observatoryId || data.observatory?.id === observatoryId) {
        if (data.type === 'status_update') {
          let cameOnline = false;
          setObservatory(prev => {
            if (!prev) return prev;
            cameOnline = prev.status !== 'online' && data.status === 'online';
            return { ...prev, status: data.status, plugin_connected: data.plugin_connected };
          });
          addLogEntry({ message: `Observatory status: ${data.status}`, severity: 'info' });
          // Plugin authentication populates capabilities / equipment_configuration
          // / filter names on the observatory document, but those aren't
          // broadcast piecemeal. Pull the fresh snapshot so the UI reflects
          // the now-connected rig instead of the pre-connect defaults.
          if (cameOnline) fetchObservatory();
        }
        if (data.type === 'connected') {
          let cameOnline = false;
          setObservatory(prev => {
            if (!prev) return prev;
            cameOnline = prev.status !== 'online';
            return { ...prev, status: 'online', plugin_connected: true };
          });
          addLogEntry({ message: 'Observatory connected', severity: 'success' });
          if (cameOnline) fetchObservatory();
        }
        if (data.type === 'disconnected') {
          setObservatory(prev => prev ? { ...prev, status: 'offline', plugin_connected: false } : prev);
          setTelemetry(null);
          setCurrentObservation(null);
          addLogEntry({ message: `Observatory disconnected: ${data.reason || 'unknown'}`, severity: 'warning' });
        }
      }
    };

    const handleObservationProgress = (data) => {
      if (data.observatory_id === observatoryId) {
        setCurrentObservation(prev => prev ? {
          ...prev,
          progress_tracking: {
            ...prev.progress_tracking,
            // Only overwrite frame counts if this event actually carried them — the
            // plugin's multi-target activation event has no frame data and would
            // otherwise reset the counters to undefined.
            ...(data.frames_captured !== undefined && { current_exposure: data.frames_captured }),
            ...(data.frames_expected !== undefined && { total_exposures: data.frames_expected }),
            ...(data.current_target_index !== undefined && { current_target_index: data.current_target_index }),
            ...(data.current_target_name !== undefined && { current_target_name: data.current_target_name }),
            ...(data.total_targets !== undefined && { total_targets: data.total_targets }),
          },
          ...(data.progress_percent !== undefined && { progress_percent: data.progress_percent }),
        } : prev);
        if (data.frames_captured !== undefined) {
          addLogEntry({
            message: `Frame ${data.frames_captured}/${data.frames_expected} captured (${data.progress_percent}%)`,
            severity: 'info',
          });
        } else if (data.current_target_index !== undefined) {
          addLogEntry({
            message: `Now observing target ${data.current_target_index}/${data.total_targets}: ${data.current_target_name || ''}`,
            severity: 'info',
          });
        }
      }
    };

    const handleObservationAssigned = (data) => {
      console.log('[DEBUG] handleObservationAssigned fired:', JSON.stringify(data));
      if (data.observatory_id === observatoryId) {
        const name = data.observation_name;
        addLogEntry({ message: name ? `Observation assigned: ${name}` : 'Observation assigned', severity: 'success' });
      }
    };

    const handleObservationStarted = (data) => {
      if (data.observatory_id === observatoryId) {
        addLogEntry({ message: data.observation_name ? `Observation started: ${data.observation_name}` : 'Observation started', severity: 'success' });
        // Refetch the current observation
        apiService.get(`/v1/observations/${data.observation_id}`)
          .then(res => {
            if (res.data?.success) setCurrentObservation(res.data.data);
          })
          .catch(() => {});
      }
    };

    const handleObservationCompleted = (data) => {
      if (data.observatory_id === observatoryId) {
        addLogEntry({ message: data.observation_name ? `Observation completed: ${data.observation_name}` : 'Observation completed', severity: 'success' });
        setCurrentObservation(null);
      }
    };

    const handleObservationFailed = (data) => {
      if (data.observatory_id && data.observatory_id !== observatoryId) return;
      addLogEntry({ message: `Observation failed${data.observation_name ? `: ${data.observation_name}` : ''}${data.reason ? ` — ${data.reason}` : ''}`, severity: 'error' });
      setCurrentObservation(null);
    };

    const handleObservationAborted = (data) => {
      if (data.observatory_id && data.observatory_id !== observatoryId) return;
      addLogEntry({ message: `Observation aborted${data.observation_name ? `: ${data.observation_name}` : ''}${data.reason ? ` — ${data.reason}` : ''}`, severity: 'warning' });
      setCurrentObservation(null);
    };

    const handleObservationCancelled = (data) => {
      if (data.observatory_id && data.observatory_id !== observatoryId) return;
      addLogEntry({ message: `Observation cancelled${data.observation_name ? `: ${data.observation_name}` : ''}${data.reason ? ` — ${data.reason}` : ''}`, severity: 'warning' });
      setCurrentObservation(null);
    };

    const handleLogEntry = (data) => {
      if (data.observatory_id && data.observatory_id !== observatoryId) return;
      // The observation_cancelled event already puts the cancel in this log, with the
      // observation's name; its observation-log entry would only repeat it.
      if (data.entry?.event_type === 'cancelled') return;
      if (data.entry) {
        addLogEntry({
          message: data.entry.message || data.entry,
          severity: data.entry.severity || data.entry.level || 'info',
          observation_id: data.observation_id,
        });
        // Update frame counts from exposure log entries
        const meta = data.entry.metadata || data.entry.details;
        if (meta && meta.completed != null) {
          setCurrentObservation(prev => {
            if (!prev || (prev._id !== data.observation_id && prev.id !== data.observation_id)) return prev;
            const total = meta.expected || prev.progress_tracking?.total_exposures || prev.expected_file_count || 0;
            const percent = total ? Math.round((meta.completed / total) * 100) : 0;
            return {
              ...prev,
              progress_tracking: {
                ...prev.progress_tracking,
                current_exposure: meta.completed,
                total_exposures: total,
              },
              frames_captured: meta.completed,
              progress_percent: percent,
            };
          });
        }
      }
    };

    // Refresh queue and current observation on lifecycle events
    const refreshObservationState = () => { fetchQueue(); fetchCurrentObservation(); };

    websocketService.on('statusChange', handleStatusChange);
    websocketService.on('telemetryUpdate', handleTelemetry);
    websocketService.on('guiderUpdate', handleGuiderUpdate);
    websocketService.on('observatoryUpdate', handleObservatoryUpdate);
    websocketService.on('observation_progress', handleObservationProgress);
    websocketService.on('observation_assigned', handleObservationAssigned);
    websocketService.on('observation_started', handleObservationStarted);
    websocketService.on('observation_completed', handleObservationCompleted);
    websocketService.on('observation_failed', handleObservationFailed);
    websocketService.on('observation_aborted', handleObservationAborted);
    websocketService.on('observation_cancelled', handleObservationCancelled);
    websocketService.on('observation_log_entry', handleLogEntry);
    // Queue/current observation refresh on lifecycle events
    websocketService.on('observation_created', refreshObservationState);
    websocketService.on('observation_updated', refreshObservationState);
    websocketService.on('observation_assigned', refreshObservationState);
    websocketService.on('observation_started', refreshObservationState);
    websocketService.on('observation_completed', refreshObservationState);
    websocketService.on('observation_failed', refreshObservationState);
    websocketService.on('observation_aborted', refreshObservationState);
    websocketService.on('observation_cancelled', refreshObservationState);

    return () => {
      websocketService.off('statusChange', handleStatusChange);
      websocketService.off('telemetryUpdate', handleTelemetry);
      websocketService.off('guiderUpdate', handleGuiderUpdate);
      websocketService.off('observatoryUpdate', handleObservatoryUpdate);
      websocketService.off('observation_progress', handleObservationProgress);
      websocketService.off('observation_assigned', handleObservationAssigned);
      websocketService.off('observation_started', handleObservationStarted);
      websocketService.off('observation_completed', handleObservationCompleted);
      websocketService.off('observation_failed', handleObservationFailed);
      websocketService.off('observation_aborted', handleObservationAborted);
      websocketService.off('observation_cancelled', handleObservationCancelled);
      websocketService.off('observation_log_entry', handleLogEntry);
      websocketService.off('observation_created', refreshObservationState);
      websocketService.off('observation_updated', refreshObservationState);
      websocketService.off('observation_assigned', refreshObservationState);
      websocketService.off('observation_started', refreshObservationState);
      websocketService.off('observation_completed', refreshObservationState);
      websocketService.off('observation_failed', refreshObservationState);
      websocketService.off('observation_aborted', refreshObservationState);
      websocketService.off('observation_cancelled', refreshObservationState);
    };
  }, [observatoryId, addLogEntry, fetchQueue, fetchCurrentObservation, fetchObservatory]);

  const clearEventLog = useCallback(() => {
    eventLogRef.current = [];
    setEventLog([]);
  }, []);

  const value = {
    observatoryId,
    observatory,
    telemetry,
    observatoryState,
    currentObservation,
    queue,
    eventLog,
    clearEventLog,
    wsStatus,
    guiderHistory,
  };

  return (
    <ObservatoryContext.Provider value={value}>
      {children}
    </ObservatoryContext.Provider>
  );
};
